import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button, Input, Modal } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import {
  validateBreaksAboveMoq,
  detectDuplicateMinQuantities,
} from "../lib/volume-breaks-validation.js";
import { VolumeBreaksEditor } from "./VolumeBreaksEditor.js";
import type { VariantDTO, VolumeBreakDTO } from "@wmart/shared-types";

interface Props {
  productId: string;
  variant: VariantDTO | null; // null = create, non-null = edit
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  title: string;
  base_price_minor: string;
  currency: string;
  stock_quantity: string;
  weight_grams: string;
  pack_size: string;
  ean: string;
  length_mm: string;
  width_mm: string;
  height_mm: string;
  min_order_quantity: string;
  order_increment: string;
}

const EAN_REGEX = /^\d{8,14}$/;

/**
 * Create or edit a product variant.
 * - Create: POST /v1/admin/products/:productId/variants
 * - Edit:   PATCH /v1/admin/products/:productId/variants/:id
 *
 * 409 with "ean" in the message surfaces as an inline error rather than a toast.
 *
 * P14a-T10: also manages MOQ, order increment, and volume_breaks diff-dispatch.
 */
export function VariantEditModal({ productId, variant, onClose, onSaved }: Props) {
  const { notify, notifyProblem } = useToast();
  const [eanError, setEanError] = useState<string | null>(null);

  const isEdit = variant !== null;

  const dims = variant?.dimensions_json;

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: variant?.title ?? "",
      base_price_minor: variant != null ? String(variant.base_price_minor) : "",
      currency: variant?.currency ?? "GBP",
      stock_quantity: variant != null ? String(variant.stock_quantity) : "0",
      weight_grams: variant?.weight_grams != null ? String(variant.weight_grams) : "",
      pack_size: variant?.pack_size ?? "",
      ean: variant?.ean ?? "",
      length_mm: dims?.length_mm != null ? String(dims.length_mm) : "",
      width_mm: dims?.width_mm != null ? String(dims.width_mm) : "",
      height_mm: dims?.height_mm != null ? String(dims.height_mm) : "",
      min_order_quantity: variant != null ? String(variant.min_order_quantity) : "1",
      order_increment: variant != null ? String(variant.order_increment) : "1",
    },
  });

  // Volume breaks state — separate from react-hook-form because per-row diff
  // dispatch needs the loaded snapshot for diffing.
  const [loadedBreaks, setLoadedBreaks] = useState<VolumeBreakDTO[]>(
    variant?.volume_breaks ?? [],
  );
  const [draftBreaks, setDraftBreaks] = useState<VolumeBreakDTO[]>(
    variant?.volume_breaks ?? [],
  );

  // When the parent reopens the modal with a different variant, sync both snapshots.
  useEffect(() => {
    setLoadedBreaks(variant?.volume_breaks ?? []);
    setDraftBreaks(variant?.volume_breaks ?? []);
  }, [variant?.id]);

  function validateEanOnBlur(value: string) {
    if (value === "") {
      setEanError(null);
      return;
    }
    if (!EAN_REGEX.test(value)) {
      setEanError("EAN must be 8–14 digits");
    } else {
      setEanError(null);
    }
  }

  async function onSubmit(values: FormValues) {
    // Client-side EAN validation before submit
    if (values.ean !== "" && !EAN_REGEX.test(values.ean)) {
      setEanError("EAN must be 8–14 digits");
      return;
    }

    // Client-side quantity-rules validation
    const moq = Number(values.min_order_quantity);
    const inc = Number(values.order_increment);
    if (moq % inc !== 0) {
      notify("error", "MOQ must be divisible by increment");
      return;
    }
    const belowMoq = validateBreaksAboveMoq(draftBreaks, moq);
    if (belowMoq.length > 0) {
      notify("error", "Volume break row(s) below MOQ");
      return;
    }
    const dupes = detectDuplicateMinQuantities(draftBreaks);
    if (dupes.length > 0) {
      notify("error", "Duplicate min_quantity in volume breaks");
      return;
    }

    const hasDims =
      values.length_mm !== "" || values.width_mm !== "" || values.height_mm !== "";

    const body: Record<string, unknown> = {
      title: values.title,
      base_price_minor: Number(values.base_price_minor),
      currency: values.currency || "GBP",
      stock_quantity: Number(values.stock_quantity),
      min_order_quantity: moq,
      order_increment: inc,
    };

    if (values.weight_grams !== "") {
      body.weight_grams = Number(values.weight_grams);
    }

    if (values.pack_size !== "") {
      body.pack_size = values.pack_size;
    }

    if (values.ean !== "") {
      body.ean = values.ean;
    }

    if (hasDims) {
      body.dimensions_json = {
        length_mm: Number(values.length_mm) || 0,
        width_mm: Number(values.width_mm) || 0,
        height_mm: Number(values.height_mm) || 0,
      };
    }

    try {
      if (!isEdit) {
        // Create mode: POST variant, then POST any draft volume breaks.
        const created = await api.post<VariantDTO>(
          `/v1/admin/products/${productId}/variants`,
          body,
        );
        // TODO(V1.6): wrap variant + volume_breaks creation in a server-side transaction
        // or implement compensating delete here. Today, a failure mid-loop leaves an
        // orphaned variant with partial breaks; the user sees the error but the variant
        // row persists.
        for (const b of draftBreaks) {
          await api.post(
            `/v1/admin/products/${productId}/variants/${created.id}/volume-breaks`,
            {
              min_quantity: b.min_quantity,
              unit_price_minor: b.unit_price_minor,
              currency: "GBP",
            },
          );
        }
        notify("success", "Variant created");
        onSaved();
        return;
      }

      // Edit mode: PATCH variant scalars (always — server skips audit if no diff).
      await api.patch(`/v1/admin/products/${productId}/variants/${variant.id}`, body);

      // Diff volume_breaks: DELETE → PATCH → POST.
      const loadedById = new Map(loadedBreaks.map((b) => [b.id, b]));
      const draftIds = new Set(
        draftBreaks.filter((b) => b.id !== "").map((b) => b.id),
      );

      // 2a. Removed rows → DELETE.
      for (const b of loadedBreaks) {
        if (!draftIds.has(b.id)) {
          await api.delete(
            `/v1/admin/products/${productId}/variants/${variant.id}/volume-breaks/${b.id}`,
          );
        }
      }

      // 2b. Updated rows → PATCH.
      for (const b of draftBreaks) {
        if (b.id === "") continue;
        const loaded = loadedById.get(b.id);
        if (
          loaded &&
          (loaded.min_quantity !== b.min_quantity ||
            loaded.unit_price_minor !== b.unit_price_minor)
        ) {
          await api.patch(
            `/v1/admin/products/${productId}/variants/${variant.id}/volume-breaks/${b.id}`,
            {
              min_quantity: b.min_quantity,
              unit_price_minor: b.unit_price_minor,
            },
          );
        }
      }

      // 2c. New rows → POST.
      for (const b of draftBreaks) {
        if (b.id === "") {
          await api.post(
            `/v1/admin/products/${productId}/variants/${variant.id}/volume-breaks`,
            {
              min_quantity: b.min_quantity,
              unit_price_minor: b.unit_price_minor,
              currency: "GBP",
            },
          );
        }
      }

      notify("success", "Variant saved");
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && /ean/i.test(err.message)) {
          setEanError("EAN already in use");
          return;
        }
        if (err.problem) {
          notifyProblem(err.problem);
          if (isEdit) {
            notify(
              "error",
              "Partial save: close and reopen the variant to retry. Avoid retrying without reload.",
            );
          }
          return;
        }
      }
      notify("error", isEdit ? "Save failed" : "Create failed");
      if (isEdit) {
        notify(
          "error",
          "Partial save: close and reopen the variant to retry. Avoid retrying without reload.",
        );
      }
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit variant — ${variant.sku}` : "Add variant"}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* SKU — server-assigned from a sequence; immutable. Shown read-only
            on edit; on create there is nothing to show yet. */}
        {isEdit ? (
          <div className="flex flex-col gap-1">
            <p className="text-label-sm text-ink-700">SKU</p>
            <p className="font-mono text-body-sm text-ink-900">{variant.sku}</p>
          </div>
        ) : null}

        {/* Title */}
        <Input
          label="Title"
          placeholder="e.g. Apple 500g"
          error={errors.title?.message}
          {...register("title", { required: "Title is required" })}
        />

        {/* Base price / currency row */}
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              type="number"
              step={1}
              min={0}
              label="Base price (minor units)"
              placeholder="e.g. 150 for £1.50"
              helper="In pence / cents"
              error={errors.base_price_minor?.message}
              {...register("base_price_minor", {
                required: "Base price is required",
                validate: (v) =>
                  (Number.isInteger(Number(v)) && Number(v) >= 0) ||
                  "Must be a non-negative integer",
              })}
            />
          </div>
          <div className="w-24">
            <Input
              label="Currency"
              maxLength={3}
              placeholder="GBP"
              error={errors.currency?.message}
              {...register("currency", {
                required: "Currency is required",
                minLength: { value: 3, message: "3 chars" },
                maxLength: { value: 3, message: "3 chars" },
              })}
            />
          </div>
        </div>

        {/* Stock quantity */}
        <Input
          type="number"
          step={1}
          min={0}
          label="Stock quantity"
          error={errors.stock_quantity?.message}
          {...register("stock_quantity", {
            validate: (v) =>
              (Number.isInteger(Number(v)) && Number(v) >= 0) ||
              "Must be a non-negative integer",
          })}
        />

        {/* Weight */}
        <Input
          type="number"
          step={1}
          min={0}
          label="Weight (grams)"
          placeholder="Optional"
          error={errors.weight_grams?.message}
          {...register("weight_grams", {
            validate: (v) =>
              v === "" ||
              (Number.isInteger(Number(v)) && Number(v) >= 0) ||
              "Must be a non-negative integer",
          })}
        />

        {/* Pack size */}
        <Input
          label="Pack size"
          placeholder="e.g. 12 x 500g (optional)"
          error={errors.pack_size?.message}
          {...register("pack_size")}
        />

        {/* EAN */}
        <div>
          <Input
            label="EAN"
            placeholder="8–14 digits (optional)"
            error={errors.ean?.message ?? eanError ?? undefined}
            {...register("ean", {
              onBlur: (e) => validateEanOnBlur((e.target as HTMLInputElement).value),
            })}
          />
        </div>

        {/* Dimensions */}
        <div>
          <p className="mb-1.5 text-label-sm text-ink-700">
            Dimensions (mm) — optional
          </p>
          <div className="flex gap-3">
            <Input
              type="number"
              step={1}
              min={0}
              label="Length"
              placeholder="mm"
              error={errors.length_mm?.message}
              {...register("length_mm", {
                validate: (v) =>
                  v === "" ||
                  (Number.isInteger(Number(v)) && Number(v) >= 0) ||
                  "Integer ≥ 0",
              })}
            />
            <Input
              type="number"
              step={1}
              min={0}
              label="Width"
              placeholder="mm"
              error={errors.width_mm?.message}
              {...register("width_mm", {
                validate: (v) =>
                  v === "" ||
                  (Number.isInteger(Number(v)) && Number(v) >= 0) ||
                  "Integer ≥ 0",
              })}
            />
            <Input
              type="number"
              step={1}
              min={0}
              label="Height"
              placeholder="mm"
              error={errors.height_mm?.message}
              {...register("height_mm", {
                validate: (v) =>
                  v === "" ||
                  (Number.isInteger(Number(v)) && Number(v) >= 0) ||
                  "Integer ≥ 0",
              })}
            />
          </div>
        </div>

        {/* Order quantity rules */}
        <section className="mt-6 border-t pt-4 flex flex-col gap-4">
          <h3 className="text-label-sm font-semibold text-ink-900">Order quantity rules</h3>
          <Input
            type="number"
            min={1}
            step={1}
            label="Minimum order qty"
            error={errors.min_order_quantity?.message}
            {...register("min_order_quantity", {
              validate: (v) =>
                (Number.isInteger(Number(v)) && Number(v) >= 1) ||
                "Must be a positive integer",
            })}
          />
          <Input
            type="number"
            min={1}
            step={1}
            label="Order increment"
            helper="MOQ must be divisible by increment"
            error={errors.order_increment?.message}
            {...register("order_increment", {
              validate: (v) =>
                (Number.isInteger(Number(v)) && Number(v) >= 1) ||
                "Must be a positive integer",
            })}
          />
        </section>

        {/* Volume pricing */}
        <section className="mt-6 border-t pt-4 flex flex-col gap-4">
          <h3 className="text-label-sm font-semibold text-ink-900">Volume pricing</h3>
          <VolumeBreaksEditor
            value={draftBreaks}
            variantMoq={Number(watch("min_order_quantity")) || 1}
            onChange={setDraftBreaks}
          />
        </section>

        {/* Hidden submit for Enter-to-submit */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
